import { Fragment, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Panel, Chip, Th, Td } from "@/components/common";
import { fmt, gbp, pnlClass, ldnTime, cur } from "@/lib/api";

export function TradeRows({ trades, prefix = "trade" }) {
  return trades.map((t) => (
    <tr key={t.id} data-testid={`${prefix}-row-${t.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
      <Td>{t.name}</Td>
      <Td><Chip status={t.direction > 0 ? "LONG" : "SHORT"} label={t.direction > 0 ? "Long" : "Short"} /></Td>
      <Td className="text-xs text-slate-400">{t.leg}</Td>
      <Td className="num">{cur()}{fmt(t.size)}</Td>
      <Td className="num">{fmt(t.entry)}</Td>
      <Td className="num">{fmt(t.exit)}</Td>
      <Td className={`num ${pnlClass(t.points)}`}>{fmt(t.points)}</Td>
      <Td className={`num font-semibold ${pnlClass(t.pnl)}`}>{gbp(t.pnl)}</Td>
      <Td className="text-xs text-slate-400">{ldnTime(t.opened_at)}</Td>
      <Td className="text-xs text-slate-400">{ldnTime(t.closed_at)}</Td>
      <Td className="num text-xs">{t.reason}</Td>
      <Td className="text-xs">{t.opened_by}{t.live ? ` / live ${t.live.status}` : ""}</Td>
    </tr>
  ));
}

export const TradeHead = () => (
  <thead className="border-b border-slate-800"><tr><Th>Instrument</Th><Th>Direction</Th><Th>Leg</Th><Th>Size</Th><Th>Entry</Th><Th>Exit</Th><Th>Points</Th><Th>{cur()}</Th><Th>Opened</Th><Th>Closed</Th><Th>Reason</Th><Th>Opened by</Th></tr></thead>
);

export function TradeHistory({ trades }) {
  return (
    <Panel title="Trade history" testid="levels-trade-history">
      {!trades?.length ? <p className="text-sm text-slate-500">No closed trades yet.</p> : (
        <div className="max-h-[480px] overflow-auto">
          <table className="w-full min-w-[1100px]"><TradeHead /><tbody><TradeRows trades={trades} /></tbody></table>
        </div>
      )}
    </Panel>
  );
}

export function DailyReport({ days }) {
  const [open, setOpen] = useState({});
  return (
    <Panel title="Daily report" testid="levels-daily-report">
      {!days?.length ? <p className="text-sm text-slate-500">One row per trading day appears here once trades close.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="border-b border-slate-800"><tr><Th /><Th>Day</Th><Th>Trades</Th><Th>Auto</Th><Th>Wins</Th><Th>Losses</Th><Th>Win %</Th><Th>Day P&L</Th><Th>Balance</Th></tr></thead>
            <tbody>
              {days.map((d) => (
                <Fragment key={d.date}>
                  <tr data-testid={`daily-row-${d.date}`} onClick={() => setOpen({ ...open, [d.date]: !open[d.date] })} className="cursor-pointer border-b border-slate-800/60 hover:bg-slate-800/40">
                    <Td><ChevronRight className={`h-4 w-4 text-slate-500 transition-transform ${open[d.date] ? "rotate-90" : ""}`} /></Td>
                    <Td className="num">{d.date}</Td><Td className="num">{d.trades}</Td><Td className="num">{d.auto}</Td><Td className="num text-emerald-400">{d.wins}</Td><Td className="num text-rose-400">{d.losses}</Td>
                    <Td className="num">{fmt(d.win_pct, 1)}%</Td><Td className={`num font-semibold ${pnlClass(d.pnl)}`}>{gbp(d.pnl)}</Td><Td className="num">{gbp(d.balance)}</Td>
                  </tr>
                  {open[d.date] && (
                    <tr><td colSpan={9} className="bg-slate-950/60 p-2"><table className="w-full"><TradeHead /><tbody><TradeRows trades={d.items} prefix="daily-trade" /></tbody></table></td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
